'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from '@/i18n/intl';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { MapPin, Mail, Phone } from 'lucide-react';
import { sendMessage } from '@/lib/actions/messages';
import { toast } from 'sonner';
import { useSettings } from '@/components/shared/SettingsProvider';
import { RichText } from '@/components/shared/RichText';

export function ContactSection() {
  const t = useTranslations('CTA');
  const tForm = useTranslations('Form');
  const tCommon = useTranslations('Common');
  const settings = useSettings();
  
  const [formData, setFormData] = useState({
    subject: 'General',
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await sendMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: `Phone: ${formData.phone}\n\n${formData.message}`,
      });

      if (result.success) {
        toast.success(tForm('success'));
        setFormData({ subject: 'General', name: '', phone: '', email: '', message: '' });
      } else {
        toast.error(tForm('error'));
      }
    } catch (error) {
      toast.error(tForm('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
      <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="p-0 lg:pr-12">
          <SectionLabel text={tCommon('getInTouch')} />
          <h2
            className="text-[32px] md:text-[40px] font-normal text-charcoal font-heading leading-snug mb-8"
          >
            {t('headline')}
          </h2>

          {settings.home_cta_description && (
            <RichText
              html={settings.home_cta_description}
              className="text-[16px] text-gray-500 mb-8 leading-relaxed font-sans [&_p]:mb-4"
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full bg-light border-b-2 border-navy px-4 py-[11px] text-[14px] text-navy focus:outline-none focus:border-primary transition-colors font-sans"
                style={{ height: '44.8px' }}
              >
                <option value="General">{tForm('subjects.general')}</option>
                <option value="Strategic Diagnosis">{tForm('subjects.diagnosis')}</option>
                <option value="Strategic Alignment">{tForm('subjects.alignment')}</option>
                <option value="Learning & Development">{tForm('subjects.build')}</option>
                <option value="Human Capital Advisory">{tForm('subjects.sustain')}</option>
                {settings.enable_career !== 'false' && (
                  <option value="Career">{tForm('subjects.career')}</option>
                )}
              </select>
            </div>
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={tForm('name')}
                className="w-full bg-light border-b-2 border-navy px-4 py-3 text-[14px] text-navy placeholder:text-gray focus:outline-none focus:border-primary transition-colors font-sans"
                required
              />
            </div>
            <div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={tForm('phone')}
                className="w-full bg-light border-b-2 border-navy px-4 py-3 text-[14px] text-navy placeholder:text-gray focus:outline-none focus:border-primary transition-colors font-sans"
              />
            </div>
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={tForm('email')}
                className="w-full bg-light border-b-2 border-navy px-4 py-3 text-[14px] text-navy placeholder:text-gray focus:outline-none focus:border-primary transition-colors font-sans"
                required
              />
            </div>
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={tForm('message')}
                rows={5}
                className="w-full bg-light border-b-2 border-navy px-4 py-3 text-[14px] text-navy placeholder:text-gray focus:outline-none focus:border-primary transition-colors resize-none font-sans"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary text-white text-[14px] font-semibold uppercase px-8 py-3 rounded-[30px] hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 font-sans cursor-pointer"
            >
              {isLoading ? tForm('submitting').toUpperCase() : tForm('submit').toUpperCase()}
            </button>
          </form>
        </div>

        <div className="bg-[#F9F9F9] p-8 lg:p-12 mt-8 lg:mt-0">
          <div className="rounded-lg overflow-hidden aspect-[16/9] mb-8 border border-gray-200 bg-gray-100 flex items-center justify-center">
            {settings.google_maps_url ? (
              <iframe
                src={settings.google_maps_url}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Simisi&Co. Office Location"
                className="w-full h-full"
              />
            ) : (
              <div className="text-gray-400 flex flex-col items-center gap-2">
                <MapPin size={32} />
                <p className="text-sm font-sans">Map location not configured</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-1" />
              <div>
                <h3 className="text-[18px] font-semibold text-navy mb-1 font-heading">{t('officeAddress')}</h3>
                <p className="text-[14px] text-gray leading-relaxed font-sans">
                  {settings.office_address || 'Jakarta, Indonesia'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Mail className="h-5 w-5 text-primary shrink-0 mt-1" />
              <div>
                <h3 className="text-[18px] font-semibold text-charcoal mb-1 font-heading">{t('email')}</h3>
                <a 
                  href={`mailto:${settings.contact_email || 'eizel@simisi.id'}`} 
                  className="text-[14px] text-primary hover:underline font-sans"
                >
                  {settings.contact_email || 'eizel@simisi.id'}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Phone className="h-5 w-5 text-primary shrink-0 mt-1" />
              <div>
                <h3 className="text-[18px] font-semibold text-charcoal mb-1 font-heading">{t('phone')}</h3>
                <a 
                  href={`tel:${(settings.contact_phone || '+6281905681347').replace(/[^0-9+]/g, '')}`} 
                  className="text-[14px] text-primary hover:underline font-sans"
                >
                  {settings.contact_phone || '+62 819-0568-1347'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
